import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * TimeInput - a custom HH:MM input that supports BOTH typing and scrolling.
 *
 * Features:
 *  - Two segments (hours, minutes), type digits directly.
 *  - Mouse wheel over a segment increments/decrements it (hover + scroll).
 *  - ArrowUp / ArrowDown keys also increment/decrement.
 *  - Auto-advances from hours to minutes when 2 digits entered.
 *  - Emits the same `value` / `onChange` contract as <Input type="time" />:
 *      onChange({ target: { value: "HH:MM" } })
 *
 * Props:
 *  value, onChange, disabled, className, "data-testid", step (minutes step, default 1)
 */
const pad = (n) => String(n).padStart(2, "0");

const parse = (val) => {
  if (!val || typeof val !== "string") return { h: "", m: "" };
  const [h = "", m = ""] = val.split(":");
  return { h: h.slice(0, 2), m: m.slice(0, 2) };
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const TimeInput = React.forwardRef(
  ({ value = "", onChange, disabled = false, className, step = 1, ...rest }, ref) => {
    const { h, m } = parse(value);
    const hourRef = React.useRef(null);
    const minRef = React.useRef(null);
    const containerRef = React.useRef(null);
    React.useImperativeHandle(ref, () => containerRef.current);

    const emit = (newH, newM) => {
      // Emit raw segment values (do NOT pre-pad). Padding/clamping happens on blur
      // or when a segment reaches 2 digits, so users can type "0" then "9" → "09".
      onChange?.({ target: { value: `${newH ?? ""}:${newM ?? ""}` } });
    };

    const setHour = (raw) => {
      const digits = (raw || "").replace(/\D/g, "").slice(0, 2);
      // Emit raw digits first so the user can keep typing.
      emit(digits, m);
      if (digits.length === 2) {
        // Both hour digits entered → clamp + pad and auto-jump to minutes.
        const n = clamp(parseInt(digits, 10), 0, 23);
        emit(pad(n), m);
        setTimeout(() => {
          minRef.current?.focus();
          minRef.current?.select?.();
        }, 0);
      }
    };

    const setMin = (raw) => {
      const digits = (raw || "").replace(/\D/g, "").slice(0, 2);
      emit(h, digits);
      if (digits.length === 2) {
        const n = clamp(parseInt(digits, 10), 0, 59);
        emit(h, pad(n));
      }
    };

    const bumpHour = (delta) => {
      const cur = parseInt(h || "0", 10) || 0;
      const next = ((cur + delta) % 24 + 24) % 24;
      emit(pad(next), m || "00");
    };

    const bumpMin = (delta) => {
      const cur = parseInt(m || "0", 10) || 0;
      const stepDelta = delta * step;
      const total = cur + stepDelta;
      // wrap minutes 0..59, but do NOT roll hours (keeps interaction predictable)
      const next = ((total % 60) + 60) % 60;
      emit(h || "00", pad(next));
    };

    const onWheel = (kind) => (e) => {
      if (disabled) return;
      // Only act if the segment is focused or hovered (which it must be to receive wheel)
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      if (kind === "h") bumpHour(dir);
      else bumpMin(dir);
    };

    const onKey = (kind) => (e) => {
      if (disabled) return;
      if (e.key === "ArrowUp") {
        e.preventDefault();
        kind === "h" ? bumpHour(1) : bumpMin(1);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        kind === "h" ? bumpHour(-1) : bumpMin(-1);
      } else if (e.key === ":" || (kind === "h" && e.key === "Tab" && !e.shiftKey)) {
        // ":" jumps to minutes
        if (e.key === ":") {
          e.preventDefault();
          minRef.current?.focus();
          minRef.current?.select?.();
        }
      } else if (kind === "m" && e.key === "Backspace" && !e.target.value) {
        e.preventDefault();
        hourRef.current?.focus();
        hourRef.current?.select?.();
      }
    };

    const onBlurSegment = (kind) => (e) => {
      if (disabled) return;
      const raw = (e.target.value || "").replace(/\D/g, "");
      if (kind === "h") {
        if (raw === "") {
          // If the user left hour empty but minutes are set, normalise to 00:MM.
          if (m !== "") emit("00", m);
          return;
        }
        const n = clamp(parseInt(raw, 10), 0, 23);
        emit(pad(n), m === "" ? "00" : (m.length === 2 ? m : pad(parseInt(m, 10) || 0)));
      } else {
        if (raw === "") {
          if (h !== "") emit(h.length === 2 ? h : pad(parseInt(h, 10) || 0), "00");
          return;
        }
        const n = clamp(parseInt(raw, 10), 0, 59);
        emit(h === "" ? "00" : (h.length === 2 ? h : pad(parseInt(h, 10) || 0)), pad(n));
      }
    };

    // Attach non-passive wheel listener so preventDefault works (React's onWheel is passive on some browsers)
    React.useEffect(() => {
      const hi = hourRef.current;
      const mi = minRef.current;
      if (!hi || !mi) return;
      const hHandler = (e) => onWheel("h")(e);
      const mHandler = (e) => onWheel("m")(e);
      hi.addEventListener("wheel", hHandler, { passive: false });
      mi.addEventListener("wheel", mHandler, { passive: false });
      return () => {
        hi.removeEventListener("wheel", hHandler);
        mi.removeEventListener("wheel", mHandler);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [h, m, disabled, step]);

    return (
      <div
        ref={containerRef}
        className={cn(
          "inline-flex items-center h-9 rounded-md border border-input bg-transparent px-2 text-base shadow-sm transition-colors focus-within:outline-none focus-within:ring-1 focus-within:ring-ring md:text-sm",
          disabled && "cursor-not-allowed opacity-50",
          className
        )}
        data-testid={rest["data-testid"]}
        onClick={(e) => {
          // clicking the container focuses the nearest segment
          if (e.target === containerRef.current) hourRef.current?.focus();
        }}
      >
        <input
          ref={hourRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          aria-label="Hours"
          maxLength={2}
          disabled={disabled}
          value={h}
          placeholder="HH"
          onChange={(e) => setHour(e.target.value)}
          onKeyDown={onKey("h")}
          onBlur={onBlurSegment("h")}
          onFocus={(e) => e.target.select()}
          className="w-7 bg-transparent text-center outline-none tabular-nums placeholder:text-muted-foreground disabled:cursor-not-allowed"
        />
        <span className="px-0.5 text-muted-foreground select-none">:</span>
        <input
          ref={minRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          aria-label="Minutes"
          maxLength={2}
          disabled={disabled}
          value={m}
          placeholder="MM"
          onChange={(e) => setMin(e.target.value)}
          onKeyDown={onKey("m")}
          onBlur={onBlurSegment("m")}
          onFocus={(e) => e.target.select()}
          className="w-7 bg-transparent text-center outline-none tabular-nums placeholder:text-muted-foreground disabled:cursor-not-allowed"
        />
      </div>
    );
  }
);

TimeInput.displayName = "TimeInput";

export { TimeInput };
